import React, { useState, useEffect, useCallback } from 'react';

import { injectIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
/* eslint frontbucket-patterns/no-old-dropdown-menu: "warn" */
import DropdownMenu, {
  DropdownItem,
  DropdownItemGroup,
} from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/glyph/more';
import { CompassLogo } from '@atlaskit/logo';
import Modal, {
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  ModalTransition,
} from '@atlaskit/modal-dialog-next';
import { FeatureKeys } from '@atlassian/bitbucket-features';
import { createProvisioningPageExtensions } from '@atlassiansox/cross-flow-extensions';
import {
  Journeys,
  Targets,
  useCrossFlow,
} from '@atlassiansox/cross-flow-support';

import { useAnalytics } from 'src/components/settings/analytics';
import { Expander } from 'src/components/sidebar';
import { useFlag } from 'src/hooks/flag';
import { useIntl } from 'src/hooks/intl';
import { useDjangoFeature } from 'src/hooks/use-django-feature';
import { usePref } from 'src/hooks/use-pref';
import {
  useCompassCloudIdForRepository,
  useCompassCloudIdForWorkspace,
} from 'src/sections/repository/sections/source/components/compass/hooks';
import { getIsWorkspaceAdmin } from 'src/selectors/global-selectors';
import { getCurrentRepoWorkspaceSlug } from 'src/selectors/repository-selectors';
import {
  getCurrentWorkspaceUUID,
  getCurrentWorkspace,
} from 'src/selectors/workspace-selectors';
import urls from 'src/urls/workspaces';
import { httpRequest } from 'src/utils/http-request';

import messages from './compass-banner.i18n';
import * as styles from './compass-banner.style';
import compassBannerUrl from './compass-banner.svg';
import compassCardUrl from './compass-empty-state-card.svg';

const PREF_KEY = 'compass-banner-home-page-v1-dismissed';

type CompassBannerProps = {
  isCard?: boolean;
};

export const CompassBanner = ({
  isCard = false,
}: CompassBannerProps): JSX.Element | null => {
  const [emailRequestLoading, setEmailRequestLoading] = useState(false);
  const intl = useIntl();
  const crossflow = useCrossFlow();
  const { showSuccessFlag } = useFlag({ enableForPdvs: true });
  const isWorkspaceAdmin = useSelector(getIsWorkspaceAdmin);
  const workspaceUuid = useSelector(getCurrentWorkspaceUUID);
  const workspaceSlug = useSelector(getCurrentWorkspace);
  const workspaceId = workspaceSlug?.slug || '';
  const repoWorkspaceSlug = useSelector(getCurrentRepoWorkspaceSlug);
  const slug = workspaceId || repoWorkspaceSlug || '';
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [hasDismissedBanner = '', setHasDismissedBanner, isLoadingPref] =
    usePref(PREF_KEY);
  const { publishButtonClickEvent, publishScreenEvent, publishTrackEvent } =
    useAnalytics({
      source: isCard ? 'compassEmptyStateCard' : 'compassBanner',
    });
  const {
    cloudId: bannerCloudId,
    isLoading: bannerCloudIdLoading,
    error: bannerCloudIdError,
  } = useCompassCloudIdForWorkspace('workspaceSlug');
  const {
    cloudId: cardCloudId,
    isLoading: cardCloudIdLoading,
    error: cardCloudIdError,
  } = useCompassCloudIdForRepository('repositoryOwner', 'repositorySlug');

  const extensions = createProvisioningPageExtensions({
    actions: {
      primary: {
        behaviour: 'land',
        prompt: 'go-to-product',
        options: {
          targetTab: 'new',
          urlParams: {
            onboarding: 'bitbucket',
          },
        },
      },
    },
  });

  const hasConnectedCompassSite = !!bannerCloudId || !!cardCloudId;

  const shouldRenderBanner =
    !isCard &&
    !isLoadingPref &&
    workspaceUuid !== undefined &&
    !hasDismissedBanner.includes(workspaceUuid) &&
    !bannerCloudIdLoading &&
    !bannerCloudIdError &&
    !hasConnectedCompassSite;

  const shouldRenderCard =
    isCard &&
    !isLoadingPref &&
    workspaceUuid !== undefined &&
    !hasDismissedBanner.includes(workspaceUuid) &&
    !cardCloudIdLoading &&
    !cardCloudIdError &&
    !hasConnectedCompassSite;

  const sendEmailNotification = async () => {
    let emailSendSuccess = true;
    setEmailRequestLoading(true);
    try {
      await httpRequest().post(
        urls.api.internal.compass_banner_email_notification(slug),
        {
          target_product: Targets.COMPASS,
          source_component: isCard
            ? 'compassEmptyStateCardRepositorySource'
            : 'compassBannerHomepage',
          source_context: 'bitbucket',
          journey: 'get-started',
          provisioning_page_options: {
            actions: {
              primary: {
                behaviour: 'land',
                prompt: 'go-to-product',
                options: {
                  targetTab: 'new',
                  urlParams: {
                    onboarding: 'bitbucket',
                  },
                },
              },
            },
          },
        }
      );

      showSuccessFlag(
        intl.formatMessage(messages.accessRequestSentDescription),
        intl.formatMessage(messages.accessRequestSentHeading)
      );
    } catch (e) {
      emailSendSuccess = false;
    } finally {
      setEmailRequestLoading(false);
    }

    publishTrackEvent({
      action: 'sent',
      actionSubject: 'email',
      actionSubjectId: isCard
        ? 'emptyStateCardRequestCompassFree'
        : 'bannerRequestCompassFree',
      attributes: {
        success: emailSendSuccess,
        workspaceAdmin: isWorkspaceAdmin,
      },
    });
  };

  const openCrossflow = async (handleOnCompletion?: () => void) => {
    if (crossflow.isEnabled) {
      const crossflowResult = await crossflow.api.open({
        targetProduct: Targets.COMPASS,
        sourceComponent: isCard
          ? 'compassEmptyStateCardRepositorySource'
          : 'compassBannerHomepage',
        sourceContext: 'bitbucket',
        journey: Journeys.GET_STARTED,
        extensions,
        extraAnalyticsAttributes: {
          workspaceAdmin: isWorkspaceAdmin,
        },
      });

      if (crossflowResult.success) {
        if (handleOnCompletion) handleOnCompletion();
      }
    }
  };

  const handleCtaClick = async (handleOnCompletion?: () => void) => {
    if (!isWorkspaceAdmin) {
      await sendEmailNotification();
      if (handleOnCompletion) handleOnCompletion();
    } else {
      if (handleOnCompletion) {
        await openCrossflow(handleOnCompletion);
      } else {
        await openCrossflow();
      }
    }
  };

  useEffect(() => {
    if (shouldRenderBanner || shouldRenderCard) {
      publishScreenEvent({
        attributes: {
          workspaceAdmin: isWorkspaceAdmin,
        },
      });
    }
  }, [
    isWorkspaceAdmin,
    publishScreenEvent,
    shouldRenderBanner,
    shouldRenderCard,
  ]);

  const handleLearnMore = () => {
    publishButtonClickEvent(
      isCard ? 'emptyStateCardLearnMoreButton' : 'bannerLearnMoreButton',
      {
        workspaceAdmin: isWorkspaceAdmin,
      }
    );
  };

  const handleDismissBanner = useCallback(() => {
    publishButtonClickEvent(
      isCard ? 'emptyStateCardDismissed' : 'bannerDismissed',
      {
        workspaceAdmin: isWorkspaceAdmin,
      }
    );
    showSuccessFlag(
      intl.formatMessage(messages.dismissedFlagDescription),
      intl.formatMessage(messages.dismissedFlagHeading)
    );
  }, [
    intl,
    isCard,
    isWorkspaceAdmin,
    publishButtonClickEvent,
    showSuccessFlag,
  ]);

  const handleDropdownModalWhyAmISeeingThis = useCallback(() => {
    publishButtonClickEvent(
      isCard
        ? 'emptyStateCardWhyAmISeeingThis'
        : 'bannerDropdownModalWhyAmISeeingThis',
      {
        workspaceAdmin: isWorkspaceAdmin,
      }
    );
  }, [isCard, isWorkspaceAdmin, publishButtonClickEvent]);

  const hideBanner = useCallback(() => {
    setHasDismissedBanner(`${hasDismissedBanner}${workspaceUuid}`);
  }, [hasDismissedBanner, setHasDismissedBanner, workspaceUuid]);

  const renderDropdownItemGroup = useCallback(() => {
    return (
      <DropdownItemGroup>
        <DropdownItem
          onClick={() => {
            hideBanner();
            handleDismissBanner();
          }}
        >
          {intl.formatMessage(messages.notInterested)}
        </DropdownItem>
        <DropdownItem
          onClick={() => {
            setModalIsOpen(true);
            handleDropdownModalWhyAmISeeingThis();
          }}
        >
          {intl.formatMessage(messages.whyAmISeeingThis)}
        </DropdownItem>
      </DropdownItemGroup>
    );
  }, [
    handleDismissBanner,
    handleDropdownModalWhyAmISeeingThis,
    hideBanner,
    intl,
  ]);

  const renderModal = useCallback(() => {
    return (
      <Modal
        shouldCloseOnEscapePress
        width="large"
        onClose={() => setModalIsOpen(false)}
      >
        <ModalHeader>
          <ModalTitle>
            {intl.formatMessage(messages.whyAmISeeingThis)}
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          <p>
            {intl.formatMessage(messages.whyAmISeeingThisModalDescription1)}
          </p>
          <p>
            {intl.formatMessage(messages.whyAmISeeingThisModalDescription2)}
          </p>
          <p>
            <a
              href="https://www.atlassian.com/legal/privacy-policy#what-this-policy-covers"
              target="_blank"
            >
              {intl.formatMessage(messages.whyAmISeeingThisModalLearnMoreLink)}
            </a>
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            appearance="primary"
            autoFocus
            onClick={() => setModalIsOpen(false)}
          >
            {intl.formatMessage(messages.whyAmISeeingThisModalOkButton)}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }, [intl]);

  if (shouldRenderBanner) {
    return (
      <>
        <styles.BannerWrapper data-testid="compass-banner">
          <styles.LeftColumn>
            <styles.BannerContentWrapper>
              <styles.BannerTitleWrapper>
                {intl.formatMessage(messages.bannerHeading)}
              </styles.BannerTitleWrapper>
              <styles.BannerDescriptionWrapper>
                {intl.formatMessage(messages.bannerDescription)}
              </styles.BannerDescriptionWrapper>
            </styles.BannerContentWrapper>
            <styles.ButtonGroupWrapper>
              <ButtonGroup>
                <LoadingButton
                  appearance="primary"
                  spacing="compact"
                  onClick={() => {
                    publishButtonClickEvent('bannerGetCompassFreeButton', {
                      workspaceAdmin: isWorkspaceAdmin,
                    });
                    handleCtaClick(hideBanner);
                  }}
                  testId="compass-banner.primary.button"
                  isLoading={emailRequestLoading}
                >
                  {isWorkspaceAdmin
                    ? intl.formatMessage(messages.requestAccessToCompass)
                    : intl.formatMessage(
                        messages.requestAccessToCompassNonAdmin
                      )}
                </LoadingButton>
                <Button
                  appearance="link"
                  spacing="compact"
                  href="https://support.atlassian.com/bitbucket-cloud/docs/integrate-bitbucket-and-compass/"
                  target="_blank"
                  testId="compass-banner.learn-more-button"
                  onClick={handleLearnMore}
                >
                  {intl.formatMessage(messages.learnMore)}
                </Button>
              </ButtonGroup>
            </styles.ButtonGroupWrapper>
          </styles.LeftColumn>
          <styles.BannerImageWrapper>
            <img alt="" src={compassBannerUrl} />
          </styles.BannerImageWrapper>
          <styles.RightColumn>
            <styles.DismissWrapper>
              <DropdownMenu
                triggerType="button"
                triggerButtonProps={{
                  appearance: 'subtle',
                  iconBefore: <MoreIcon label="" />,
                  'aria-label': 'Menu',
                }}
                testId="compass-banner.dropdown-menu.trigger"
                position="bottom right"
              >
                {renderDropdownItemGroup()}
              </DropdownMenu>
            </styles.DismissWrapper>
            <styles.LogoWrapper>
              <CompassLogo size="small" appearance="brand" />
            </styles.LogoWrapper>
          </styles.RightColumn>
        </styles.BannerWrapper>
        <ModalTransition>{modalIsOpen && renderModal()}</ModalTransition>
      </>
    );
  }

  if (shouldRenderCard) {
    return (
      <>
        <Expander
          label=""
          isLoading={!shouldRenderCard}
          isPanelBodyOverflowVisible
          panelBodyPadding="0"
          dataTestId="compass-empty-state-card"
          isCollapsed={false}
          shouldRenderHeader={false}
        >
          <styles.CardContentWrapper>
            <styles.CardHeader>
              <styles.CardDropdownMenu>
                <DropdownMenu
                  triggerType="button"
                  triggerButtonProps={{
                    style: { padding: '4px' },
                    appearance: 'subtle',
                    iconBefore: <MoreIcon label="" />,
                    'aria-label': `${intl.formatMessage(messages.cardMenu)}`,
                  }}
                  testId="compass-empty-state-card.dropdown-menu.trigger"
                  position="bottom right"
                >
                  {renderDropdownItemGroup()}
                </DropdownMenu>
              </styles.CardDropdownMenu>
              <styles.CardTitleWrapper>
                {intl.formatMessage(messages.cardHeading)}
              </styles.CardTitleWrapper>
            </styles.CardHeader>
            <styles.CardImgWrapper>
              <img alt="" src={compassCardUrl} />
            </styles.CardImgWrapper>
            <styles.CardDescriptionWrapper>
              {intl.formatMessage(messages.bannerDescription)}
              <Button
                appearance="link"
                spacing="compact"
                href="https://support.atlassian.com/bitbucket-cloud/docs/integrate-bitbucket-and-compass/"
                target="_blank"
                testId="compass-empty-state-card.learn-more-button"
                onClick={handleLearnMore}
                style={{ paddingLeft: '0px' }}
              >
                {intl.formatMessage(messages.learnMore)}
              </Button>
            </styles.CardDescriptionWrapper>
            <styles.CardButtonWrapper>
              <LoadingButton
                appearance="default"
                onClick={() => {
                  publishButtonClickEvent(
                    'emptyStateCardGetCompassFreeButton',
                    {
                      workspaceAdmin: isWorkspaceAdmin,
                    }
                  );
                  handleCtaClick(hideBanner);
                }}
                testId="compass-empty-state-card.primary.button"
                isLoading={emailRequestLoading}
              >
                {isWorkspaceAdmin
                  ? intl.formatMessage(messages.requestAccessToCompass)
                  : intl.formatMessage(messages.requestAccessToCompassNonAdmin)}
              </LoadingButton>
            </styles.CardButtonWrapper>
          </styles.CardContentWrapper>
        </Expander>
        <ModalTransition>{modalIsOpen && renderModal()}</ModalTransition>
      </>
    );
  }

  return null;
};

/**
 * @param isCard - true will render the card version of the banner else the banner/longer version.
 */
const CompassBannerWithFF = ({
  isCard = false,
}: CompassBannerProps): JSX.Element | null => {
  const disableCompassBitbucketRepositoryCard = useDjangoFeature(
    FeatureKeys.disableCompassBitbucketRepositoryCard
  );
  if (disableCompassBitbucketRepositoryCard) {
    return null;
  }
  return <CompassBanner isCard={isCard} />;
};

export default injectIntl(CompassBannerWithFF);
